import { getCountryName } from "@/features/flight-estimator/_logic/iso-country-utils";

import { PRIMARY_AIRPORTS } from "./airport-ranking";
import { AREA_LIST_ALL, areaCaption } from "./areas";
import { ASSISTANT_TYPO_MIN, MAX_ORIGIN_COUNT, PLACE_OPTIONS_SHOWN, TYPO_LEAD } from "./constants";
import { canonicalCity, cityNameParts, cityNames, placeKey } from "./city-names";
import { CA_PROVINCES, MAJOR_HUBS, NATIVE_NAMES, PLACE_ALIASES, US_STATES } from "./regions";

/** A scheduled airport as the place search reads it. */
export interface AirportRecord {
  iata: string;
  name: string;
  /** The airport's city, as the airport data spells it. */
  city: string;
  /** ISO alpha-2 country. */
  country: string;
  /** ISO 3166-2 subdivision, e.g. "US-TX". */
  region: string;
  type: "large_airport" | "medium_airport";
  /** Other names the airport goes by ("Heathrow", "O'Hare", "NYC"). */
  keywords: string[];
}

/** How a search result matched, from most to least certain. */
export type PlaceMatch = "alias" | "code" | "exact" | "prefix" | "airport" | "contains" | "region" | "fuzzy";

/** A real place a planner can pick: a city with the airports that serve it. */
export interface PlaceOption {
  /** City name as the airport data spells it, e.g. "San Diego". */
  city: string;
  /** ISO alpha-2 country. */
  country: string;
  countryName: string;
  /** "TX" for US and Canadian cities, the country name elsewhere. */
  context: string;
  /** IATA codes, the one fares are priced from first. */
  codes: string[];
  /** How the search matched it; Enter only takes confident matches. */
  match?: PlaceMatch;
  /** For a typo match, how close it is, 0 to 1. */
  typo?: number;
  /** For a region or a town without flights: what it stands for ("SF, Oakland, San Jose", "via SFO · ~1h drive"). */
  note?: string;
}

/** A place with what the search ranks it by. */
interface IndexedPlace {
  option: PlaceOption;
  key: string;
  region: string;
  /** Bigger airports, more of them: large counts 3, medium 1. */
  size: number;
  /** Folded airport names and keywords. */
  aliases: string[];
  /** Folded names the city goes by: English, local and the airport data's spelling. */
  names: string[];
}

/** Airports that share a metro with the city's main airport, shown together ("JFK/LGA/EWR"). */
export const METRO_CODES: Readonly<Record<string, string[]>> = {
  "new york": ["JFK", "LGA", "EWR"],
  london: ["LHR", "LGW", "STN", "LCY", "LTN"],
  chicago: ["ORD", "MDW"],
  washington: ["IAD", "DCA", "BWI"],
  paris: ["CDG", "ORY"],
  tokyo: ["HND", "NRT"],
  "san francisco": ["SFO", "OAK", "SJC"],
  "los angeles": ["LAX", "BUR", "LGB", "SNA"],
  houston: ["IAH", "HOU"],
  dallas: ["DFW", "DAL"],
  milan: ["MXP", "LIN"],
};

const CONFIDENT: ReadonlySet<PlaceMatch> = new Set(["alias", "code", "exact", "prefix", "airport"]);
const RESOLVABLE: ReadonlySet<PlaceMatch> = new Set(["alias", "code", "exact", "airport"]);

/**
 * Lowercases, strips accents and punctuation and collapses spaces, so "São Paulo", "sao  paulo"
 * and "O'Hare" compare as people mean them.
 * @param text - Any name.
 * @returns The folded name.
 * @throws Never.
 */
export const fold = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['’.]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const trigrams = (text: string): Set<string> => {
  const padded = `  ${text} `;
  const grams = new Set<string>();
  for (let index = 0; index < padded.length - 2; index += 1) grams.add(padded.slice(index, index + 3));
  return grams;
};

const trigramSimilarity = (a: string, b: string): number => {
  const left = trigrams(a);
  const right = trigrams(b);
  let shared = 0;
  left.forEach((gram) => right.has(gram) && (shared += 1));
  return shared / (left.size + right.size - shared);
};

const editDistance = (a: string, b: string): number => {
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const current = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length];
};

/** How close a typo is to a name, 0 to 1. */
const typoScore = (query: string, name: string): number =>
  Math.max(trigramSimilarity(query, name), 1 - editDistance(query, name) / Math.max(query.length, name.length));

/** Up to 10 extra for a city whose airports are among the busiest, busiest highest. */
const hubBonus = (codes: readonly string[]): number => {
  const ranks = codes.map((code) => MAJOR_HUBS.indexOf(code)).filter((rank) => rank >= 0);
  return ranks.length ? 10 * (1 - Math.min(...ranks) / MAJOR_HUBS.length) : 0;
};

const regionName = (region: string): string | null => US_STATES[region] ?? CA_PROVINCES[region] ?? null;

const contextOf = (country: string, region: string): string =>
  country === "US" || country === "CA" ? (region.split("-")[1] ?? country) : (getCountryName(country) ?? country);

/**
 * Builds the place index from scheduled airports: one place per city and country (and per state
 * or province in the US and Canada, so Portland, OR and Portland, ME stay apart), its main
 * airport first (the city's primary, else a large one), metro airports listed with it.
 * @param airports - Scheduled large and medium airports.
 * @returns The index the search ranks.
 * @throws Never.
 */
export const buildPlaceIndex = (airports: readonly AirportRecord[]): IndexedPlace[] => {
  const byCode = new Map(airports.map((airport) => [airport.iata, airport]));
  const groups = new Map<string, AirportRecord[]>();
  for (const airport of airports) {
    if (!airport.city || !airport.iata) continue;
    // Group by the city's canonical name, so "Venezia (VE)" and "Venice" are one place.
    const state = airport.country === "US" || airport.country === "CA" ? airport.region : "";
    const key = `${placeKey(airport.city)}|${airport.country}|${state}`;
    groups.set(key, [...(groups.get(key) ?? []), airport]);
  }
  // A group like "Dallas-Fort Worth" whose airports a metro already lists ("Dallas · DFW/DAL")
  // would show the same airports twice, so it folds into that metro.
  const absorbed = (members: AirportRecord[]): boolean =>
    Object.entries(METRO_CODES).some(
      ([metro, codes]) =>
        placeKey(members[0].city) !== metro &&
        placeKey(members[0].city).includes(metro) &&
        members.every((airport) => codes.includes(airport.iata))
    );
  const cities = [...groups.values()]
    .filter((members) => !absorbed(members))
    .map((members) => {
      const lead = members.find((airport) => airport.type === "large_airport") ?? members[0];
      const name = canonicalCity(lead.city);
      const key = placeKey(name);
      const primary = PRIMARY_AIRPORTS[key];
      const metro = (METRO_CODES[key] ?? []).filter((code) => byCode.get(code)?.country === lead.country);
      const large = new Set(
        members.filter((airport) => airport.type === "large_airport").map((airport) => airport.iata)
      );
      const codes = [...new Set([...members.map((airport) => airport.iata), ...metro])].sort(
        (a, b) =>
          Number(b === primary) - Number(a === primary) ||
          Number(large.has(b) || byCode.get(b)?.type === "large_airport") -
            Number(large.has(a) || byCode.get(a)?.type === "large_airport") ||
          metro.indexOf(a) - metro.indexOf(b)
      );
      return {
        key,
        region: lead.region,
        size:
          Math.min(
            members.reduce((sum, airport) => sum + (airport.type === "large_airport" ? 3 : 1), 0),
            10
          ) + hubBonus(codes),
        aliases: members.flatMap((airport) => [fold(airport.name), ...airport.keywords.map(fold)]).filter(Boolean),
        names: [
          ...new Set(
            [...cityNames(name), ...members.flatMap((airport) => [airport.city, ...cityNameParts(airport.city)])].map(
              fold
            )
          ),
        ],
        option: {
          city: name,
          country: lead.country,
          countryName: getCountryName(lead.country) ?? lead.country,
          context: contextOf(lead.country, lead.region),
          codes,
        },
      };
    });
  // Regions and towns without scheduled flights, priced through their airports ("Bay Area", "Napa").
  const known = new Set(cities.map((place) => place.key));
  const areas = AREA_LIST_ALL.filter((area) => !known.has(placeKey(area.name))).map((area) => ({
    key: placeKey(area.name),
    region: area.region,
    size: Math.min(area.airports.filter((code) => byCode.get(code)?.type === "large_airport").length * 3, 10),
    aliases: [],
    names: [...new Set([area.name, ...(area.aliases ?? [])].map(fold))],
    option: {
      city: area.name,
      country: area.country,
      countryName: getCountryName(area.country) ?? area.country,
      context: contextOf(area.country, area.region),
      codes: area.airports,
      note: areaCaption(area.name) ?? undefined,
    },
  }));
  return [...cities, ...areas];
};

/** Search results, with a heading when they list a state's or country's cities. */
export interface PlaceResults {
  heading: string | null;
  options: PlaceOption[];
}

const byRegion = (index: readonly IndexedPlace[], query: string): PlaceResults | null => {
  if (query.length < 4) return null;
  const matches = (name: string) => fold(name) === query || (query.length >= 5 && fold(name).startsWith(query));
  // "New York State" and "Washington State" only match in full, so "new york" stays the city.
  const region = Object.entries({ ...US_STATES, ...CA_PROVINCES }).find(([, name]) => matches(name))?.[0];
  const country = region
    ? null
    : [...new Set(index.map((place) => place.option.country))].find((code) => {
        const name = getCountryName(code);
        return name ? matches(name) : false;
      });
  if (!region && !country) return null;
  const options = index
    .filter((place) => (region ? place.region === region : place.option.country === country))
    .sort((a, b) => b.size - a.size || a.option.city.localeCompare(b.option.city))
    .slice(0, PLACE_OPTIONS_SHOWN)
    .map((place) => ({ ...place.option, match: "region" as const }));
  const name = region ? (regionName(region) ?? region) : (getCountryName(country!) ?? country!);
  return options.length ? { heading: `Cities in ${name}`, options } : null;
};

/**
 * Ranks places for what a planner typed: aliases and metros ("nyc", "sf", "dc"), exact airport
 * codes, city names (exact, then prefix, biggest airports first), airport names ("Heathrow",
 * "O'Hare"), states, provinces and countries ("texas", "portugal"), and typos ("chicgo"). A code
 * only counts when typed exactly, so "texas" never surfaces Telluride's TEX.
 * @param query - What was typed; "City, Country" narrows to that country.
 * @param index - The place index.
 * @returns The best places, each marked with how it matched.
 * @throws Never.
 */
export const searchPlaceIndex = (query: string, index: readonly IndexedPlace[]): PlaceResults => {
  // A native-script name ("東京") reads as its English one; an airport in brackets ("Tokyo (Haneda)") is dropped.
  const english = NATIVE_NAMES[query.trim()] ?? query;
  const [namePart, ...countryParts] = english.replace(/\([^)]*\)/g, " ").split(",");
  const q = fold(namePart);
  const countryFilter = fold(countryParts.join(" "));
  if (q.length < 2) return { heading: null, options: [] };
  const inCountry = (place: IndexedPlace) =>
    !countryFilter ||
    fold(place.option.countryName) === countryFilter ||
    fold(place.option.country) === countryFilter ||
    fold(place.option.context) === countryFilter;

  const region = countryFilter ? null : byRegion(index, q);

  const alias = PLACE_ALIASES[q] ? fold(PLACE_ALIASES[q]) : null;
  const code = /^[a-z]{3}$/.test(q) ? q.toUpperCase() : null;
  const scored = index
    .filter(inCountry)
    .map((place): { place: IndexedPlace; score: number; match: PlaceMatch; typo?: number } | null => {
      const city = place.key;
      const words = city.split(" ");
      if (alias && city === alias) return { place, score: 1000 + place.size, match: "alias" };
      if (code && place.option.codes[0] === code) return { place, score: 950, match: "code" };
      if (code && place.option.codes.includes(code)) return { place, score: 900, match: "code" };
      if (city === q || place.names.includes(q)) return { place, score: 800 + place.size * 5, match: "exact" };
      if (city.startsWith(q) || place.names.some((name) => name.startsWith(q)))
        return { place, score: 600 + place.size * 5, match: "prefix" };
      if (q.length >= 2 && words.some((word) => word.startsWith(q)))
        return { place, score: 550 + place.size * 5, match: "prefix" };
      if (
        q.length >= 4 &&
        place.aliases.some(
          (name) => name === q || name.split(" ").some((word) => word === q || (q.length >= 5 && word.startsWith(q)))
        )
      )
        return { place, score: 500 + place.size * 5, match: "airport" };
      if (q.length >= 4 && city.includes(q)) return { place, score: 400 + place.size * 5, match: "contains" };
      if (q.length >= 4) {
        const typo = Math.max(typoScore(q, city), ...words.map((word) => typoScore(q, word)));
        if (typo >= 0.6) return { place, score: 200 + typo * 100 + place.size * 3, match: "fuzzy", typo };
      }
      return null;
    })
    .filter(
      (entry): entry is { place: IndexedPlace; score: number; match: PlaceMatch; typo?: number } => entry !== null
    )
    .sort((a, b) => b.score - a.score || a.place.option.city.localeCompare(b.place.option.city));
  const named = scored.filter((entry) => entry.match === "alias" || entry.match === "exact");
  if (region) {
    // A name that is both a city and a state ("Washington") lists the city first, then the state's cities.
    if (!named.length) return region;
    const cities = named.map(({ place, match }) => ({ ...place.option, match }));
    const seen = new Set(cities.map((option) => `${option.city}|${option.context}`));
    const rest = region.options.filter((option) => !seen.has(`${option.city}|${option.context}`));
    return { heading: null, options: [...cities, ...rest].slice(0, PLACE_OPTIONS_SHOWN) };
  }
  return {
    heading: null,
    options: scored.slice(0, PLACE_OPTIONS_SHOWN).map(({ place, match, typo }) => ({ ...place.option, match, typo })),
  };
};

/**
 * Whether Enter may take a result without the planner choosing it: an alias, exact code, exact
 * city, city prefix or airport name. Typos, states and countries need a deliberate pick.
 * @param option - A search result.
 * @returns True when it is a confident match.
 * @throws Never.
 */
export const isConfident = (option: PlaceOption): boolean => (option.match ? CONFIDENT.has(option.match) : false);

/**
 * The place a typed name unambiguously means, for text that didn't come from the picker (the
 * assistant's edits, a pasted list): an alias, an exact airport code, an exact city ("City,
 * Country" narrows it) or an airport's name. Partial or misspelled names ("San Fr", "Saygo")
 * match nothing, so they can't be added. With `allowTypos` (the assistant), a single very close
 * misspelling resolves too ("shangha" is Shanghai; "Saygo" is still nothing).
 * @param query - What was typed.
 * @param index - The place index.
 * @param options - `allowTypos` for the assistant's edits.
 * @returns The place, or null.
 * @throws Never.
 */
export const matchPlace = (
  query: string,
  index: readonly IndexedPlace[],
  options: { allowTypos?: boolean } = {}
): PlaceOption | null => {
  const results = searchPlaceIndex(query, index).options;
  const top = results[0];
  if (top?.match && RESOLVABLE.has(top.match)) return top;
  if (!options.allowTypos || !top) return null;
  // The assistant may pass a near-certain slip: a name one letter short ("shangha") or a close
  // typo ("chicgo"), with no rival that is nearly as close.
  const second = results[1];
  if (top.match === "prefix") {
    const nearlyWhole = fold(query).length / fold(top.city).length >= ASSISTANT_TYPO_MIN;
    return nearlyWhole && second?.match !== "prefix" ? top : null;
  }
  const clear = !second || (second.typo ?? 0) < (top.typo ?? 0) - TYPO_LEAD;
  return top.match === "fuzzy" && (top.typo ?? 0) >= ASSISTANT_TYPO_MIN && clear ? top : null;
};

/**
 * How an option reads in the picker: "Austin, TX · AUS", "Porto, Portugal · OPO",
 * "New York, NY · JFK/LGA/EWR".
 * @param option - The option.
 * @returns The label.
 * @throws Never.
 */
export const placeLabel = (option: PlaceOption): string =>
  `${option.city}, ${option.context} · ${option.codes.slice(0, 3).join("/")}`;

/**
 * Where a picked place is, as the cost index reads it: "US-OR" for a US or Canadian city, the ISO
 * country ("GR") elsewhere.
 * @param option - The picked place.
 * @returns The region.
 * @throws Never.
 */
export const placeRegion = (option: Pick<PlaceOption, "country" | "context">): string =>
  (option.country === "US" || option.country === "CA") && option.context !== option.country
    ? `${option.country}-${option.context}`
    : option.country;

/** One home city and head count read from pasted text. */
export interface PastedTraveler {
  city: string;
  count: number;
}

/** Folded names of countries, states and provinces, and their codes, which follow a city after a comma. */
const REGION_NAMES: ReadonlySet<string> = (() => {
  const names = new Set<string>(["usa", "us", "uk", "uae"]);
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  for (const first of letters) {
    for (const second of letters) {
      const name = getCountryName(`${first}${second}`);
      if (name) names.add(fold(name));
    }
  }
  for (const [code, name] of Object.entries({ ...US_STATES, ...CA_PROVINCES })) {
    names.add(fold(name));
    names.add(fold(code.split("-")[1] ?? ""));
  }
  return names;
})();

/** A count with an optional "people" word: "8", "1,500", "8 people". */
const COUNT = String.raw`(\d{1,3}(?:,\d{3})+|\d+)`;
const PEOPLE = String.raw`(?:people|persons|ppl|pax|travell?ers|attendees|x)`;
const TRAILING = new RegExp(String.raw`^(.*?)[\s:(-]+${COUNT}\)?\s*${PEOPLE}?$`, "i");
const LEADING = new RegExp(String.raw`^${COUNT}\s*${PEOPLE}?\s*(?:from|in)?\s+(.+)$`, "i");

const toCount = (digits: string): number => Number(digits.replace(/,/g, ""));

/** Reads one entry: "Austin 7", "Austin: 1,500", "8 San Diego", "12 people from Boston", "Lisbon". */
const readEntry = (entry: string): PastedTraveler | null => {
  const trailing = entry.match(TRAILING);
  const leading = entry.match(LEADING);
  const [city, count] = trailing?.[1]?.trim()
    ? [trailing[1], toCount(trailing[2])]
    : leading
      ? [leading[2], toCount(leading[1])]
      : [entry, 1];
  const name = city.replace(/[\s:,-]+$/, "").trim();
  if (!name || /^[\d,]+$/.test(name)) return null;
  return { city: name, count: Math.min(Math.max(1, count), MAX_ORIGIN_COUNT) };
};

/**
 * Reads a pasted list of home cities and counts: "San Diego 8, Austin 7", "8 San Diego",
 * "12 people from Boston", one per line, or tab-separated columns from a spreadsheet. Entries
 * split on new lines, semicolons and commas, except a comma inside a number ("1,500") or before
 * a country, state or province ("Paris, France 4", "Portland, ME 3"), which stays with its city.
 * An entry without a number counts 1.
 * @param text - The pasted text.
 * @returns The entries, in order; same cities are not merged here.
 * @throws Never.
 */
export const parsePastedTravelers = (text: string): PastedTraveler[] =>
  text
    .split(/[\n;]+/)
    .flatMap((line) => {
      const pieces = line
        .replace(/\t+/g, " ")
        .split(/,(?!\d{3}\b)/)
        .map((piece) => piece.trim())
        .filter(Boolean);
      const entries: string[] = [];
      for (const piece of pieces) {
        const region = fold(piece.replace(new RegExp(String.raw`[\s:(-]*${COUNT}\)?\s*${PEOPLE}?$`, "i"), ""));
        const previous = entries[entries.length - 1];
        // "Paris, France 4": a country after a city without a count belongs to that city.
        if (previous !== undefined && !/\d/.test(previous) && REGION_NAMES.has(region)) {
          entries[entries.length - 1] = `${previous}, ${piece}`;
        } else {
          entries.push(piece);
        }
      }
      return entries;
    })
    .map(readEntry)
    .filter((entry): entry is PastedTraveler => entry !== null);
